import {
  Percent, TrendingUp, Divide, Cake, CalendarDays, Clock, Tag,
  Wallet, PiggyBank, LineChart, Landmark, Utensils, Ruler, DollarSign, HeartPulse,
} from 'lucide-react';

export const CATEGORIES = [
  { id: 'math', name: 'Math', description: 'Everyday number crunching, percentages and fractions.', color: 'from-emerald-500 to-teal-500' },
  { id: 'money', name: 'Money', description: 'Personal finance, loans, savings, and shopping.', color: 'from-amber-500 to-orange-500' },
  { id: 'time', name: 'Time & Date', description: 'Ages, durations, and date arithmetic.', color: 'from-sky-500 to-blue-500' },
  { id: 'conversion', name: 'Conversion', description: 'Units and currencies.', color: 'from-violet-500 to-fuchsia-500' },
  { id: 'health', name: 'Health', description: 'Simple health estimators.', color: 'from-rose-500 to-pink-500' },
];

export const TOOLS = [
  { slug: 'percentage-calculator', name: 'Percentage Calculator', category: 'math', icon: Percent,
    seoTitle: 'Percentage Calculator – Find X% of Y',
    tagline: 'Find X% of Y, what percent A is of B, and more.',
    description: 'A fast percentage calculator for the three most common percent problems: percent of a number, one number as a percent of another, and reverse percentages.' },
  { slug: 'percentage-change-calculator', name: 'Percentage Increase / Decrease', category: 'math', icon: TrendingUp,
    seoTitle: 'Percentage Change Calculator',
    tagline: 'Compare two values and get the percent change.',
    description: 'Calculate percentage increase or decrease between an original and a new value with a full worked breakdown.' },
  { slug: 'fractions-calculator', name: 'Fractions Calculator', category: 'math', icon: Divide,
    seoTitle: 'Fraction Calculator – Add, Subtract & More',
    tagline: 'Add, subtract, multiply, and divide fractions.',
    description: 'Perform arithmetic on two fractions and see the simplified result plus the decimal equivalent.' },
  { slug: 'age-calculator', name: 'Age Calculator', category: 'time', icon: Cake,
    seoTitle: 'Age Calculator – Years, Months & Days',
    tagline: 'Exact age in years, months, and days.',
    description: 'Calculate a precise age from a date of birth to today or to any other date.' },
  { slug: 'date-calculator', name: 'Date Calculator', category: 'time', icon: CalendarDays,
    seoTitle: 'Date Calculator – Add or Subtract Days',
    tagline: 'Add or subtract days from any date.',
    description: 'Add or subtract days, weeks, months, or years from a start date and see the resulting date.' },
  { slug: 'time-calculator', name: 'Time Duration Calculator', category: 'time', icon: Clock,
    seoTitle: 'Time Duration Calculator – Hours & Minutes',
    tagline: 'Duration between two times, in hours and minutes.',
    description: 'Compute the difference between a start and end time, useful for timesheets and travel planning.' },
  { slug: 'discount-calculator', name: 'Discount Calculator', category: 'money', icon: Tag,
    seoTitle: 'Discount Calculator – Sale Price & Savings',
    tagline: 'Sale price after a percent off.',
    description: 'Find the discounted price and total savings from an original price and a percentage discount.' },
  { slug: 'salary-calculator', name: 'Salary Converter', category: 'money', icon: Wallet,
    seoTitle: 'Salary Calculator – Hourly to Annual Pay',
    tagline: 'Convert between hourly, weekly, monthly and annual pay.',
    description: 'Convert gross pay between hourly, weekly, monthly, and annual figures using configurable working hours.' },
  { slug: 'savings-calculator', name: 'Savings Calculator', category: 'money', icon: PiggyBank,
    seoTitle: 'Savings Calculator – Deposits & Interest',
    tagline: 'Project a savings goal with regular deposits.',
    description: 'See how a starting balance grows with monthly deposits and compound interest over time.' },
  { slug: 'interest-calculator', name: 'Compound Interest Calculator', category: 'money', icon: LineChart,
    seoTitle: 'Compound Interest Calculator',
    tagline: 'Simple and compound interest projections.',
    description: 'Calculate simple or compound interest earnings on an initial principal over a chosen period.' },
  { slug: 'loan-calculator', name: 'Loan Calculator', category: 'money', icon: Landmark,
    seoTitle: 'Loan Calculator – Payment & Interest',
    tagline: 'Monthly payment and total interest on a loan.',
    description: 'Estimate the monthly payment, total interest, and total repayment on a fixed-rate installment loan.' },
  { slug: 'tip-calculator', name: 'Tip Calculator', category: 'money', icon: Utensils,
    seoTitle: 'Tip Calculator – Tips & Split Bills',
    tagline: 'Split a bill and add a tip fairly.',
    description: 'Add a tip percentage to a bill and split the total across any number of people.' },
  { slug: 'unit-converter', name: 'Unit Converter', category: 'conversion', icon: Ruler,
    seoTitle: 'Unit Converter – Metric & Imperial Units',
    tagline: 'Length, weight, volume, and temperature.',
    description: 'Convert between metric and imperial units for length, weight, volume, and temperature.' },
  { slug: 'currency-converter', name: 'Currency Converter', category: 'conversion', icon: DollarSign,
    seoTitle: 'Currency Converter – Live Exchange Rates',
    tagline: 'Convert amounts using live daily rates.',
    description: 'Convert between world currencies using indicative daily exchange rates. Not for trading.' },
  { slug: 'bmi-calculator', name: 'BMI Calculator', category: 'health', icon: HeartPulse,
    seoTitle: 'BMI Calculator – Metric & Imperial',
    tagline: 'Body Mass Index in metric or imperial.',
    description: 'Calculate BMI from height and weight in metric or imperial units, with the standard adult category ranges.' },
];

export const getToolBySlug = (slug) => TOOLS.find((t) => t.slug === slug);
export const getToolsByCategory = (id) => TOOLS.filter((t) => t.category === id);
export const getCategory = (id) => CATEGORIES.find((c) => c.id === id);
export const getRelatedTools = (slug, limit = 4) => {
  const current = getToolBySlug(slug);
  if (!current) return [];
  const sameCat = TOOLS.filter((t) => t.category === current.category && t.slug !== slug);
  const rest = TOOLS.filter((t) => t.category !== current.category && t.slug !== slug);
  return [...sameCat, ...rest].slice(0, limit);
};
